import React, { useEffect, useRef, useState } from 'react';
import { useSmartWord } from 'src/hook/global/useSmartWord';
import { Word } from 'src/model/Word';
import { VideosData } from '../../../../model/WordData';
import styles from './Video.module.scss';

interface VideoProps {
    data: VideosData;
    word: Word;
}

declare global {
    interface Window {
        YT: any;
        onYouTubeIframeAPIReady: () => void;
    }
}

export const Video: React.FC<VideoProps> = ({ data, word }) => {
    const videoRef = useRef<HTMLIFrameElement>(null);
    const { HighlightWord } = useSmartWord(true);
    const [isApiReady, setIsApiReady] = useState(false);

    const getEnabledApiUrl = (url: string) => {
        let privacyUrl = url.replace('youtube.com', 'youtube-nocookie.com');
        const separator = privacyUrl.includes('?') ? '&' : '?';
        return `${privacyUrl}${separator}enablejsapi=1&origin=${window.location.origin}&widget_referrer=${window.location.href}`;
    };

    useEffect(() => {
        // Load YouTube IFrame API
        if (!window.YT) {
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

            window.onYouTubeIframeAPIReady = () => {
                setIsApiReady(true);
            };
        } else {
            setIsApiReady(true);
        }
    }, []);

    useEffect(() => {
        if (!isApiReady || !videoRef.current) return;

        const video = videoRef.current;
        let player: any;

        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== 'https://www.youtube-nocookie.com') return;

            try {
                const data = JSON.parse(event.data);
                if (data.event === 'onReady') {
                    console.log('YouTube API is ready');
                }
            } catch (e) {
                console.error('Error handling YouTube message:', e);
            }
        };

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (video && video.contentWindow && !entry.isIntersecting) {
                    try {
                        const command = JSON.stringify({
                            event: 'command',
                            func: 'pauseVideo',
                            args: ''
                        });
                        video.contentWindow.postMessage(command, '*');
                    } catch (e) {
                        console.error('Error sending pause command:', e);
                    }
                }
            },
            { threshold: 0.5 }
        );

        window.addEventListener('message', handleMessage, { passive: true });
        observer.observe(video);

        return () => {
            window.removeEventListener('message', handleMessage);
            observer.unobserve(video);
            if (player) {
                player.destroy();
            }
        };
    }, [isApiReady]);

    return (
        <div className={styles.video}>
            <div className={styles.videoPlayer}>
                <iframe
                    ref={videoRef}
                    width="100%"
                    height="250"
                    src={getEnabledApiUrl(data.VideoUrl)}
                    title={data.Title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                />
            </div>
            <p className={styles.description}>
                ...{HighlightWord(data.Description, word.Text)}
            </p>
        </div>
    );
};

export default Video;